import type { FC } from 'react'
import type { SortOption } from 'services/Search/utils/sort'

import { SortingAscending16, SortingDescending16 } from '@sok/design-system'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortParam } from 'services/Search/hooks/use-sort-param'
import { SortOrder, sortToQueryValue } from 'services/Search/utils/sort'
import styled from 'styled-components'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'

import { filterStyles } from './Filters'
import { SortDrawer } from './sort/SortDrawer'

const StyledI = styled.i<{ bottom: boolean }>(({ bottom, theme }) => ({
  /* stylelint-disable-next-line property-no-unknown */
  [bottom ? 'bottom' : 'top']: '1rem',
  backgroundColor: theme.colors.secondary,
  borderRadius: '50%',
  height: '0.5rem',
  position: 'absolute',
  right: '0.75rem',
  width: '0.5rem',
}))

interface SortProps {
  className?: string
  sortOptions: SortOption[]
}

const SortComponent: FC<SortProps> = ({ className, sortOptions }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const handleOpen = useCallback(() => void setIsOpen(true), [])
  const handleClose = useCallback(() => void setIsOpen(false), [])
  const [currentSort] = useSortParam()
  const { params, replaceParam } = useURLSearchParams({ push: false })

  /** Make sure active sort is also visible in the URL */
  useEffect(() => {
    if (params.has('sort')) return
    const sortVal = sortToQueryValue(currentSort)
    if (sortVal) {
      replaceParam('sort', sortVal)
    }
  }, [currentSort, params, replaceParam])

  const buttonIcon = useMemo(
    () =>
      !currentSort[1] || currentSort[1] === SortOrder.Desc ? (
        <SortingDescending16 />
      ) : (
        <SortingAscending16 />
      ),
    [currentSort],
  )

  const notDefaultSort = !!currentSort[0]

  return (
    <>
      <button className={className} data-test-id="search-sort-button" onClick={handleOpen}>
        <span>{t`Sort`}</span>
        {buttonIcon}
        {notDefaultSort && <StyledI bottom={currentSort[1] === SortOrder.Asc} />}
      </button>
      <SortDrawer open={isOpen} onClose={handleClose} sortOptions={sortOptions} />
    </>
  )
}

SortComponent.displayName = 'Sort'

export const Sort = styled(SortComponent)(filterStyles, ({ theme }) => ({
  position: 'relative',

  [`&:hover ${StyledI}`]: {
    backgroundColor: theme.color.highlightHoverColor,
  },
}))
